import React, { useState } from "react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  FaShoppingCart, 
  FaDollarSign, 
  FaUsers, 
  FaStore,
  FaChevronLeft,
  FaChevronRight,
  FaMoneyBillWave,
  FaHourglassHalf,
  FaCoins
} from 'react-icons/fa';
import FormFilterDate from "../../components/common/FormFilterDate";
import { getReportsData } from "../../services/reportsService";
import { toastError } from "../../components/common/Toast";
import "../../styles/pages/ReportesPage.css";

function ReportesPage() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  const handleDateFilter = async (data) => {
    try {
      setLoading(true);
      const response = await getReportsData(data.date_from, data.date_to);
      
      if (response?.success && response?.data) {
        setReportData(response.data);
      } else {
        toastError('No se pudieron obtener los datos de reportes');
      }
    } catch (error) {
      console.error('Error cargando reportes:', error);
      toastError('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c'];

  const nextProduct = () => {
    if (reportData?.topProducts?.length > 0) {
      setCurrentProductIndex((prev) => 
        (prev + 1) % reportData.topProducts.length
      );
    }
  };

  const prevProduct = () => {
    if (reportData?.topProducts?.length > 0) {
      setCurrentProductIndex((prev) => 
        prev === 0 ? reportData.topProducts.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="reportes-page">
      <div className="mb-4">
        <h2 className="page-title">Reportes de Ventas</h2>
        <p className="text-muted">Visualiza estadísticas y métricas de ventas</p>
      </div>

      {/* Filtro de fechas */}
      <FormFilterDate handleInfo={handleDateFilter} />

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Generando reportes...</p>
        </div>
      )}

      {!loading && reportData && (
        <>
          {/* ── Fila 1: Cards financieras principales ── */}
          <div className="row mt-4 g-3">

            {/* Total Vendido */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="stats-card">
                <div className="stats-icon bg-success">
                  <FaDollarSign />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Total Vendido</h6>
                  <h3 className="stats-value">{formatCurrency(reportData.stats.total_sales)}</h3>
                  <small className="text-muted">Monto total facturado</small>
                </div>
              </div>
            </div>

            {/* Acreditado en Caja */}
            {reportData.cashFlow && (
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="stats-card">
                  <div className="stats-icon bg-primary">
                    <FaMoneyBillWave />
                  </div>
                  <div className="stats-content">
                    <h6 className="stats-label">Acreditado en Caja</h6>
                    <h3 className="stats-value">{formatCurrency(reportData.cashFlow.total_credited)}</h3>
                    <small className="text-muted">Dinero en caja</small>
                  </div>
                </div>
              </div>
            )}

            {/* Pendiente Acreditación */}
            {reportData.cashFlow && (
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="stats-card">
                  <div className="stats-icon bg-info">
                    <FaHourglassHalf />
                  </div>
                  <div className="stats-content">
                    <h6 className="stats-label">Pendiente Acreditación</h6>
                    <h3 className="stats-value">{formatCurrency(reportData.cashFlow.pending_accreditation)}</h3>
                    <small className="text-muted">Cobrado, no ingresado a caja</small>
                  </div>
                </div>
              </div>
            )}

            {/* Costo Financiero Total */}
            {reportData.cashFlow && (
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="stats-card">
                  <div className="stats-icon bg-warning">
                    <FaCoins />
                  </div>
                  <div className="stats-content">
                    <h6 className="stats-label">Costo Financiero Total</h6>
                    <h3 className="stats-value">{formatCurrency(reportData.cashFlow.total_financial_cost)}</h3>
                    <small className="text-muted">CF acumulado del período</small>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Fila 2: Cards secundarias ── */}
          <div className="row mt-3 g-3">

            {/* Cantidad de pedidos */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="stats-card">
                <div className="stats-icon bg-secondary">
                  <FaShoppingCart />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Total Pedidos</h6>
                  <h3 className="stats-value">{reportData.stats.total_orders}</h3>
                  <small className="text-muted">Pedidos realizados</small>
                </div>
              </div>
            </div>

            {/* Ventas Mayoristas */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="stats-card">
                <div className="stats-icon bg-info">
                  <FaUsers />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Ventas Mayoristas</h6>
                  <h3 className="stats-value">{reportData.stats.wholesale_orders}</h3>
                  <small className="text-muted">Pedidos mayoristas</small>
                </div>
              </div>
            </div>

            {/* Ventas Físicas */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="stats-card">
                <div className="stats-icon bg-warning">
                  <FaStore />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Ventas Físicas</h6>
                  <h3 className="stats-value">{reportData.stats.physical_sales}</h3>
                  <small className="text-muted">Tienda física</small>
                </div>
              </div>
            </div>

          </div>

          {/* Estadísticas por Vendedor */}
          {reportData.sellers && reportData.sellers.length > 0 && (
            <div className="row mt-4">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title mb-4">📊 Estadísticas por Vendedor</h5>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Vendedor</th>
                            <th className="text-center">Total Pedidos</th>
                            <th className="text-end">Total Ventas</th>
                            <th className="text-center">Mayoristas</th>
                            <th className="text-center">Minoristas</th>
                            <th className="text-end">Promedio por Pedido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.sellers.map((seller, index) => (
                            <tr key={seller.seller_id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="badge bg-primary me-2">{index + 1}</div>
                                  <strong>{seller.seller_name}</strong>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-info">{seller.total_orders}</span>
                              </td>
                              <td className="text-end">
                                <strong className="text-success">{formatCurrency(seller.total_sales)}</strong>
                              </td>
                              <td className="text-center">{seller.wholesale_orders}</td>
                              <td className="text-center">{seller.retail_orders}</td>
                              <td className="text-end text-muted">
                                {formatCurrency(seller.avg_order_value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="row mt-4 g-4">{/* Gráfico de barras - Ventas por producto */}
            <div className="col-12 col-lg-8">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-4">Cantidad de ventas por producto</h5>
                  {reportData.salesByProduct?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={reportData.salesByProduct}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="product_name" 
                          type="category" 
                          width={90}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="quantity" fill="#8884d8" name="Cantidad" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No hay datos de productos en este período
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card de productos destacados */}
            <div className="col-12 col-lg-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-4">Productos destacados</h5>
                  {reportData.topProducts?.length > 0 ? (
                    <div className="featured-product-slider">
                      <div className="featured-product-content">
                        <div className="product-rank">
                          #{currentProductIndex + 1}
                        </div>
                        <h4 className="product-name">
                          {reportData.topProducts[currentProductIndex].product_name}
                        </h4>
                        <div className="product-stats">
                          <div className="stat-item">
                            <span className="stat-label">Cantidad vendida:</span>
                            <span className="stat-value">
                              {reportData.topProducts[currentProductIndex].quantity}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Pedidos:</span>
                            <span className="stat-value">
                              {reportData.topProducts[currentProductIndex].orders}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="slider-controls">
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={prevProduct}
                          disabled={reportData.topProducts.length <= 1}
                        >
                          <FaChevronLeft />
                        </button>
                        <span className="slider-indicator">
                          {currentProductIndex + 1} / {reportData.topProducts.length}
                        </span>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={nextProduct}
                          disabled={reportData.topProducts.length <= 1}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No hay productos destacados
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ventas por Canal */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-4">Cantidad de ventas por canal de venta</h5>
                  {reportData.salesByChannel?.length > 0 ? (
                    <div className="row">
                      <div className="col-lg-6">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={reportData.salesByChannel}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ channel_name, percent }) => 
                                `${channel_name}: ${(percent * 100).toFixed(0)}%`
                              }
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="sales_count"
                            >
                              {reportData.salesByChannel.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="col-lg-6">
                        <div className="shipping-list">
                          {reportData.salesByChannel.map((item, index) => (
                            <div key={index} className="shipping-item">
                              <div className="shipping-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                              <div className="shipping-info">
                                <span className="shipping-name">{item.channel_name}</span>
                                <span className="shipping-count badge bg-secondary">{item.sales_count} ventas</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No hay datos de canales en este período
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Envíos por tipo */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-4">Cantidad de envíos por forma de envío</h5>
                  {reportData.shippingsByType?.length > 0 ? (
                    <div className="row">
                      <div className="col-lg-6">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={reportData.shippingsByType}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ delivery_type, percent }) => 
                                `${delivery_type}: ${(percent * 100).toFixed(0)}%`
                              }
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="shipments"
                            >
                              {reportData.shippingsByType.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="col-lg-6">
                        <div className="shipping-list">
                          {reportData.shippingsByType.map((item, index) => (
                            <div key={index} className="shipping-item">
                              <div className="shipping-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                              <div className="shipping-info">
                                <span className="shipping-name">{item.delivery_type}</span>
                                <span className="shipping-count badge bg-secondary">{item.shipments} envíos</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No hay datos de envíos en este período
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && !reportData && (
        <div className="text-center py-5">
          <p className="text-muted">Selecciona un rango de fechas para ver los reportes</p>
        </div>
      )}
    </div>
  );
}

export default ReportesPage;
